<template>
    <div class="input__field">
        <Label :required="required" :label="label" />
        <div class="counter-container">
            <button
                type="button"
                class="counter-button counter-button--minus"
                :disabled="currentValue <= min"
                @click="decrement"
            >
                -
            </button>
            <input
                type="number"
                :value="currentValue"
                :min="min"
                :max="max"
                class="counter-input"
                :class="{ 'input__error': error }"
                @input="updateValue"
                @blur="validateValue"
            />
            <button
                type="button"
                class="counter-button counter-button--plus"
                :disabled="currentValue >= max"
                @click="increment"
            >
                +
            </button>
        </div>
        <Error :error="error" />
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Label from "@/Components/Forms/Label.vue";
import Error from "@/Components/Forms/Error.vue";

interface Props {
    modelValue: number | null;
    label: string;
    min?: number;
    max?: number;
    step?: number;
    required?: boolean;
    error?: string;
}

interface Emits {
    (e: "update:modelValue", value: number): void;
}

const props = withDefaults(defineProps<Props>(), {
    min: 0,
    max: 50,
    step: 1,
    required: false,
    error: undefined,
});

const emit = defineEmits<Emits>();

// Valeur courante avec fallback sur min si null
const currentValue = computed(() => props.modelValue ?? props.min);

const increment = () => {
    const newValue = currentValue.value + props.step;
    if (newValue <= props.max) {
        emit("update:modelValue", newValue);
    }
};

const decrement = () => {
    const newValue = currentValue.value - props.step;
    if (newValue >= props.min) {
        emit("update:modelValue", newValue);
    }
};

const updateValue = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const value = parseInt(input.value) || 0;
    emit("update:modelValue", value);
};

const validateValue = (event: Event) => {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value) || 0;

    // S'assurer que la valeur est dans les limites
    if (value < props.min) value = props.min;
    if (value > props.max) value = props.max;

    emit("update:modelValue", value);
};
</script>

<style lang="scss" scoped>
.counter-container {
    display: flex;
    align-items: center;
    border: 1px solid $grey-input;
    border-radius: 4px;
    background: white;
    max-height: 3rem; // Même hauteur que les autres inputs
    width: 100%; // S'adapte à l'espace disponible
}

.counter-button {
    background: #f8f9fa;
    border: none;
    width: 40px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: bold;
    cursor: pointer;
    color: #495057;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: #e9ecef;
        color: #212529;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &--minus {
        border-radius: 4px 0 0 4px;
    }

    &--plus {
        border-radius: 0 4px 4px 0;
    }

    &:focus {
        outline: none;
        border-color: $dark-bg;
    }
}

.counter-input {
    flex: 1;
    text-align: center;
    padding: 0.75rem; // Même padding que les autres inputs
    font-family: $bebas; // Même font que les autres inputs
    font-size: 1rem; // Même taille que les autres inputs
    font-weight: 400;
    letter-spacing: 0.08em;
    border: none;
    border-left: 1px solid $grey-input;
    border-right: 1px solid $grey-input;
    background: white;
    outline: none;

    // Supprimer les flèches du navigateur pour les inputs number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type=number] {
        -moz-appearance: textfield;
    }

    &:focus {
        outline: none;
    }

    &.input__error {
        color: $error;
    }
}
</style>
